import * as React from 'react';
import { cn } from '@/lib/cn';

// UI_BUILD_SPEC §2.3. Error styling uses the danger ramp, not accent (Suzuki
// Red), per §1.1.

const fieldClassName = (hasError: boolean) => cn(
  'w-full rounded-md border bg-white text-base text-ink shadow-card transition-colors duration-fast',
  'placeholder:text-ink-subtle',
  'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:shadow-none',
  'disabled:cursor-not-allowed disabled:bg-surface-subtle disabled:opacity-60',
  'read-only:bg-surface-subtle',
  hasError
    ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500'
    : 'border-line-strong hover:border-brand-300 focus:border-brand-500 focus:ring-brand-500',
);

interface FieldShellProps {
  id: string;
  label?: string;
  required?: boolean;
  /** Format guidance shown before the user errs (§2.3) — not a substitute for it. */
  hint?: string;
  error?: string;
  children: React.ReactNode;
}

function FieldShell({ id, label, required, hint, error, children }: FieldShellProps) {
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="mb-1.5 block text-base font-medium text-ink-muted">
          {label}
          {required && <span className="ml-0.5 text-accent-500" aria-hidden="true">*</span>}
        </label>
      )}
      {children}
      {hint && !error && <p id={`${id}-hint`} className="mt-1 text-xs text-ink-subtle">{hint}</p>}
      {error && (
        <p id={`${id}-error`} className="mt-1 text-xs text-danger-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, error, hint, id, required, ...props }, ref) => {
    // Fall back to a generated id so a `label` is always programmatically
    // associated with its input, even when the caller omits an explicit id.
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    return (
      <FieldShell id={inputId} label={label} required={required} hint={hint} error={error}>
        <input
          type={type}
          id={inputId}
          className={cn(fieldClassName(Boolean(error)), 'h-10 px-3', className)}
          ref={ref}
          required={required}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          {...props}
        />
      </FieldShell>
    );
  },
);
Input.displayName = 'Input';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, id, required, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    return (
      <FieldShell id={inputId} label={label} required={required} hint={hint} error={error}>
        <textarea
          id={inputId}
          className={cn(fieldClassName(Boolean(error)), 'min-h-24 resize-y px-3 py-2', className)}
          ref={ref}
          required={required}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          {...props}
        />
      </FieldShell>
    );
  },
);
Textarea.displayName = 'Textarea';
